import { useState, useCallback, useEffect, useMemo } from "react";
import {
  COLLECT_INFO_SUBMISSION_TIME,
  COLLECT_INFO_SUBMIT,
  useMetrics,
} from "@/components/FeatureFlags/useMetrics";
import {
  Organization,
  StudentPersonalInfoViewModel,
  TeacherPersonalInfoViewModel,
  VerificationService,
} from "../../../lib/types/types";
import { logger } from "../../../lib/utils/logger/logger";
import { handleSubmitVerificationHelpRequest } from "../../../lib/ServerApi/VerificationHelpRequestApi";
import { EmailLookupResponse, EmailLookupStatus } from "./types";
import { getEmailValidationError } from "../../../lib/validators/validators";
import { useFlag } from "../../FeatureFlags/hooks";
import { getOrgSearchUrl } from "../../../lib/ProgramTheme/programThemeGetters";
import { getDomainFromEmail } from "../../../lib/utils/routing/Url";
import { fetchOrganization } from "../../../lib/ServerApi/OrganizationApiClient";

export function useEmailFirstExperiment(programId: string, email: string, segment: string) {
  const [isLoading, setIsLoading] = useState(false);
  const [emailLookupResponse, setEmailLookupResponse] = useState<EmailLookupResponse | null>(null);
  const emailFirstCollectInfoStep = useFlag("collect-info-step-email-first");
  const isEmailUpTop = emailFirstCollectInfoStep === "no-message-dimmed-fields";
  const [bypassEmailLookup, setBypassEmailLookup] = useState(false);
  const [hasBlurred, setHasBlurred] = useState(false);
  const [hasKeyPressed, setHasKeyPressed] = useState(false);
  const [emailInputState, setEmailInputState] = useState({
    emailLookupResponse: null,
    hasBlurred: false,
    hasKeyPressed: false,
    isValidEmail: !getEmailValidationError(email),
  });
  const { userDidConvert } = useMetrics({ segment });

  const updateEmailState = useCallback((newEmail: string) => {
    const isValid = !getEmailValidationError(newEmail);
    setEmailInputState((prevState) => ({
      ...prevState,
      emailLookupResponse: newEmail !== "" && isValid ? null : prevState.emailLookupResponse,
      hasBlurred: false,
      hasKeyPressed: false,
      isValidEmail: isValid,
    }));
  }, []);

  useEffect(() => {
    updateEmailState(email);
  }, [email, updateEmailState]);

  const enableFormFields = useMemo(
    () =>
      email !== "" &&
      emailInputState.isValidEmail &&
      (!isEmailUpTop || emailLookupResponse?.status === EmailLookupStatus.NOT_FOUND),
    [email, emailInputState.isValidEmail, isEmailUpTop, emailLookupResponse],
  );

  const showFormFields = useMemo(
    () =>
      (email === "" ||
        (typeof email === "string" && email.length >= 1) ||
        emailLookupResponse?.status === null) &&
      (emailLookupResponse?.status === EmailLookupStatus.NOT_FOUND ||
        ![
          EmailLookupStatus.ALREADY_INITIATED,
          EmailLookupStatus.FOUND,
          EmailLookupStatus.PENDING,
          EmailLookupStatus.REJECTED,
        ].includes(emailLookupResponse?.status)),
    [email, emailLookupResponse],
  );

  const emailOnBlur = useCallback(async () => {
    if (!emailInputState.isValidEmail || bypassEmailLookup || hasKeyPressed) return;
    setHasBlurred(true);
    setBypassEmailLookup(false);
    setIsLoading(true);
    try {
      const response = await handleSubmitVerificationHelpRequest(programId, email);
      if (response.status === EmailLookupStatus.FOUND) {
        userDidConvert(COLLECT_INFO_SUBMIT, COLLECT_INFO_SUBMISSION_TIME);
      }
      setEmailLookupResponse(response);
    } catch (error) {
      logger.error("Error during email lookup:", error);
    } finally {
      setIsLoading(false);
    }
  }, [programId, email, bypassEmailLookup, hasKeyPressed, emailInputState.isValidEmail]);

  const emailOnKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (hasBlurred) return;
      if (event.key === "Enter") {
        setHasKeyPressed(true);
        emailOnBlur();
      }
    },
    [emailOnBlur, hasBlurred],
  );

  const handleResendClick = useCallback(async () => {
    setIsLoading(true);
    try {
      const response: EmailLookupResponse = await handleSubmitVerificationHelpRequest(
        programId,
        email,
      );
      setEmailLookupResponse(response);
    } catch (error) {
      logger.error("Error resending email:", error);
    } finally {
      setIsLoading(false);
    }
  }, [programId, email, segment]);

  const handleOnClickProceedHere = useCallback(async () => {
    if (!emailInputState.isValidEmail) return;
    setBypassEmailLookup(true);
    setEmailLookupResponse({
      status: EmailLookupStatus.NOT_FOUND,
      message: "makeChanges",
    });
  }, [emailInputState.isValidEmail]);

  const getOrgByDomain = useCallback(
    async (verificationService: VerificationService): Promise<Organization | undefined> => {
      if (!verificationService) return undefined;

      const orgSearchUrl = getOrgSearchUrl(
        verificationService.viewModel as
          | StudentPersonalInfoViewModel
          | TeacherPersonalInfoViewModel,
        verificationService.programTheme,
      );

      const emailDomain = getDomainFromEmail(email);
      const orgDomainMatch = new URL(orgSearchUrl);
      orgDomainMatch.searchParams.set("domain", emailDomain);

      const response = await fetchOrganization(orgDomainMatch.toString());

      return response && response.length === 1 ? response[0] : undefined;
    },
    [email],
  );

  if (!isEmailUpTop) {
    return {
      isEmailUpTop,
      isEmailValidationLoading: false,
      showOtherFields: true,
      disableOtherFields: false,
      emailLookupResponse: null,
      showEmailLookupResponseMessages: false,
      showFormFields: true,
      emailOnBlur: () => {},
      emailOnKeyDown: () => {},
      handleResendClick: () => Promise.resolve(),
      emailFirstCollectInfoStep,
      isValidEmail: !getEmailValidationError(email),
      getOrgByDomain: async () => undefined,
      enableFormFields: true,
    };
  }

  return {
    isEmailUpTop,
    isEmailValidationLoading: isLoading,
    emailLookupResponse,
    showEmailLookupResponseMessages: !!emailLookupResponse,
    showFormFields,
    emailOnBlur,
    emailOnKeyDown,
    handleResendClick,
    emailFirstCollectInfoStep,
    isValidEmail: emailInputState.isValidEmail,
    getOrgByDomain,
    enableFormFields,
    handleOnClickProceedHere,
  };
}
